import type { GetStaticProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { SiteContent } from '@/types'

import { useCallback, useEffect, useState } from 'react'
import { Heart } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { loadContent } from '@/lib/content'

import EnvelopeGate from '@/components/landing/envelope-gate'
import FinaleSection from '@/components/landing/finale-section'
import GallerySection from '@/components/landing/gallery-section'
import GamesSection from '@/components/landing/games-section'
import HeroSection from '@/components/landing/hero-section'
import LetterSection from '@/components/landing/letter-section'
import PromisesSection from '@/components/landing/promises-section'
import ReasonsSection from '@/components/landing/reasons-section'
import SiteFooter from '@/components/landing/site-footer'
import SongSection from '@/components/landing/song-section'
import StorySection from '@/components/landing/story-section'
import VideoSection from '@/components/landing/video-section'

const OPENED_KEY = 'tisha-surprise-opened'

interface HomeProps {
    content: SiteContent | null
}

const ContentUnavailable: FC = (): ReactNode => {
    return (
        <div className='flex min-h-svh flex-col items-center justify-center gap-5 bg-night px-6 text-center text-cream'>
            <Heart aria-hidden className='size-10 animate-heartbeat fill-petal text-petal motion-reduce:animate-none' />

            <h1 className='font-script text-6xl text-[#ffd1dc]'>
                Happy Birthday
            </h1>

            <p className='max-w-sm text-lg text-cream/75'>
                Surprise abhi taiyaar ho raha hai. Thodi der mein phir se kholna.
            </p>
        </div>
    )
}

const Home: FC<HomeProps> = ({ content }): ReactNode => {
    const [opened, setOpened] = useState(false)
    const [gateVisible, setGateVisible] = useState(true)
    const [gateKey, setGateKey] = useState(0)

    useEffect(() => {
        try {
            if (window.sessionStorage.getItem(OPENED_KEY) === '1') {
                setGateVisible(false)
                setOpened(true)
            }
        }
        catch {
            // Storage can be blocked, the envelope simply shows again.
        }
    }, [])

    const handleOpen = useCallback(() => {
        setOpened(true)

        try {
            window.sessionStorage.setItem(OPENED_KEY, '1')
        }
        catch {
            // Storage can be blocked, nothing else depends on it.
        }
    }, [])

    const handleReplay = useCallback(() => {
        try {
            window.sessionStorage.removeItem(OPENED_KEY)
        }
        catch {
            // Storage can be blocked, the replay still works.
        }

        window.scrollTo({ top: 0, behavior: 'auto' })
        setOpened(false)
        setGateVisible(true)
        setGateKey(key => key + 1)
    }, [])

    if (!content) {
        return (
            <Layout>
                <Seo />
                <ContentUnavailable />
            </Layout>
        )
    }

    const { settings, sections, story, gallery, reasons, openWhen, quiz, promises } = content
    const clips = content.clips ?? []
    const hasLetter = Boolean(settings?.letter_body)
    const hasSong = Boolean(settings && (settings.song_title || settings.song_file || settings.song_youtube_url))
    const hasVideo = Boolean(settings?.memories_video || settings?.message_video || gallery.length > 0 || clips.length > 0)

    const order: Array<[string, boolean]> = [
        ['story', story.length > 0],
        ['gallery', gallery.length > 0],
        ['video', hasVideo],
        ['reasons', reasons.length > 0],
        ['letter', hasLetter],
        ['song', hasSong],
        ['games', true]
    ]

    const nextId = order.find(([, present]) => present)?.[0]

    return (
        <Layout
            footer={(
                <div inert={!opened}>
                    <SiteFooter settings={settings} />
                </div>
            )}
        >
            <Seo
                ogImage={settings?.share_image ?? undefined}
                ogImageAlt={settings?.share_image_alt ?? undefined}
            />

            {gateVisible && (
                <EnvelopeGate
                    key={gateKey}
                    settings={settings}
                    requiresCode={content.requiresCode}
                    numericCode={content.numericCode}
                    onOpen={handleOpen}
                />
            )}

            <div inert={!opened}>
                <HeroSection settings={settings} opened={opened} nextId={nextId} />

                {story.length > 0 && <StorySection section={sections.story} moments={story} />}

                {gallery.length > 0 && <GallerySection section={sections.gallery} photos={gallery} />}

                {hasVideo && (
                    <VideoSection
                        section={sections.video}
                        clipsSection={sections.clips}
                        settings={settings}
                        photos={gallery}
                        clips={clips}
                    />
                )}

                {reasons.length > 0 && <ReasonsSection section={sections.reasons} reasons={reasons} />}

                {settings && hasLetter && <LetterSection section={sections.letter} settings={settings} />}

                {settings && hasSong && <SongSection section={sections.song} settings={settings} />}

                <GamesSection sections={sections} settings={settings} quiz={quiz} openWhen={openWhen} />

                {promises.length > 0 && <PromisesSection section={sections.promises} promises={promises} />}

                <FinaleSection section={sections.finale} settings={settings} onReplay={handleReplay} />
            </div>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    try {
        const content = await loadContent()

        return { props: { content }, revalidate: 10 }
    }
    catch (error) {
        console.error('The birthday content could not be loaded:', error instanceof Error ? error.message : error)

        const building = process.env.NEXT_PHASE === 'phase-production-build'

        // On the live site, failing here keeps the last good version of the page.
        if (!building && process.env.NODE_ENV === 'production') throw error

        return { props: { content: null }, revalidate: 10 }
    }
}

export default Home
